import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Compass, Heart, Eye, Target, Lightbulb, Users, CheckCircle2 } from "lucide-react";
import { IMAGES } from "@/lib/data";

const MISSION_ITEMS = [
  "Real-time visibility", "Operational control", "Decision-making speed", "Productivity",
  "Safety awareness", "Workflow efficiency", "Training effectiveness", "Business intelligence",
  "Long-term scalability", "Return on technology investment",
];

const THINKING_QUESTIONS = [
  "What problem is the organization truly trying to solve?",
  "What decisions are currently delayed or unclear?",
  "Where is manual effort slowing the business?",
  "Where are risks, inefficiencies, or visibility gaps?",
  "What data already exists, and what is missing?",
  "How should the system support users, managers, and leadership?",
  "How can the solution continue to improve over time?",
];

const HOW_WE_WORK_STEPS = [
  { num: "01", title: "Understand the Business Reality", desc: "We study the operational problem, business goal, user needs, workflow, constraints, and expected outcome." },
  { num: "02", title: "Define the Right System Strategy", desc: "We identify what kind of system is truly required – not just what features need to be developed." },
  { num: "03", title: "Engineer with Practicality", desc: "We design and build systems that are usable, scalable, secure, and aligned with real-world operations." },
  { num: "04", title: "Validate Through Outcomes", desc: "We focus on whether the solution improves visibility, speed, accuracy, safety, productivity, or decision-making." },
  { num: "05", title: "Improve and Scale", desc: "We help organizations evolve the system as needs grow, data improves, and operations mature." },
];

const DIFFERENTIATORS = [
  { title: "We Understand Both Technology and Operations", desc: "We do not look at projects only from a coding perspective. We study the operational problem behind the requirement." },
  { title: "We Combine Multiple Advanced Disciplines", desc: "Our team can bring together enterprise systems, AI, IoT, 3D, simulation, dashboards, automation, and user experience into one connected solution." },
  { title: "We Build for Business Value", desc: "Our focus is not only delivery. Our focus is helping clients achieve measurable improvements in visibility, efficiency, safety, productivity, and decision-making." },
  { title: "We Think Long Term", desc: "We design systems that can grow with the organization, adapt to future needs, and support continuous improvement." },
  { title: "We Are Ambitious About Quality", desc: "NGXP is built with the ambition to deliver technology at a level that can compete globally in thinking, engineering, design, and execution." },
];

const COMMITMENT_ITEMS = [
  "Clear understanding of business goals", "Strong technical ownership",
  "Thoughtful solution architecture", "Scalable engineering approach",
  "User-focused design thinking", "Transparent communication",
  "Long-term improvement mindset", "Commitment to measurable value",
];

const TEAM_SKILLS = [
  "Artificial Intelligence", "Enterprise Systems", "Data Engineering", "IoT & Embedded Systems",
  "3D & Simulation", "XR & Digital Twins", "Solution Architecture", "User Experience",
];

export default function About() {
  return (
    <div data-testid="about-page">

      {/* ===== SECTION 1: HERO ===== */}
      <section className="relative bg-white pt-6 md:pt-10 pb-12 md:pb-16 overflow-hidden">
        <div className="absolute inset-0 grid-pattern" />
        <div className="glow-blob bg-[#7B2CBF]/20 w-[500px] h-[500px] -top-32 -right-20 pulse-glow" />
        <div className="container-ngxp relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <span className="overline">About NGXP</span>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-[#0A0B10]">
                Built to Engineer the Future of <span className="gradient-text-glow">Intelligent Operations</span>
              </h1>
              <p className="mt-6 text-lg text-gray-600 leading-relaxed max-w-2xl">
                NGXP Technologies is a future-focused technology company helping enterprises transform complex operational challenges into intelligent, scalable, and business-driven solutions.
              </p>
              <p className="mt-4 text-base text-gray-500 leading-relaxed max-w-2xl">
                We are built for organizations that want more than technology delivery – they want a partner capable of understanding complexity, engineering intelligently, and building systems that create long-term operational value.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/services" className="btn-primary-gradient" data-testid="about-capabilities">
                  Explore Our Capabilities <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/contact#contact-form" className="btn-secondary-light" data-testid="about-cta">
                  Start a Strategic Discussion <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="relative rounded-3xl overflow-hidden border border-gray-100 shadow-2xl">
                <img src="/2.gif" alt="Command center" className="w-full h-[420px] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-tr from-[#0A0B10]/50 via-transparent to-transparent" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SECTION 2: WHO WE ARE ===== */}
      <section className="bg-[#F8F9FA] py-14 md:py-20">
        <div className="container-ngxp">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5 reveal">
              <span className="overline">Who We Are</span>
              <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[#0A0B10] leading-tight">
                Next Generation <span className="gradient-text-glow">eXPerience</span>
              </h2>
            </div>
            <div className="lg:col-span-7 reveal space-y-5 text-gray-600 leading-relaxed">
              <p>NGXP Technologies, also known as Next Generation eXPerience, is a technology company founded with a clear purpose: to build advanced systems that help organizations operate smarter, faster, safer, and more efficiently.</p>
              <p>Headquartered in Kerala, India, NGXP brings together a multidisciplinary team across artificial intelligence, enterprise systems, data engineering, IoT, embedded systems, 3D, simulation, XR, digital twin development, and enterprise solution delivery.</p>
              <p>We work with organizations that face complex operational challenges – where visibility, speed, safety, productivity, decision-making, and long-term scalability matter.</p>
              <div className="p-5 rounded-2xl bg-white border border-gray-100 shadow-sm">
                <p className="font-display text-lg font-semibold text-[#0A0B10]">
                  NGXP exists to turn complex business and operational problems into intelligent, practical, and future-ready systems.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SECTION 3: OUR ORIGIN ===== */}
      <section className="bg-white py-14 md:py-20">
        <div className="container-ngxp">
          <div className="max-w-4xl mx-auto reveal">
            <span className="overline">Our Origin</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[#0A0B10]">
              Why NGXP Was <span className="gradient-text-glow">Created</span>
            </h2>
            <div className="mt-8 space-y-5 text-gray-600 leading-relaxed text-lg">
              <p>NGXP was founded on the belief that the next generation of enterprise growth will not come from isolated software systems alone.</p>
              <p>Modern organizations need technology partners who can understand real-world operations, connect business logic with engineering, and build systems that help teams make better decisions.</p>
              <p>From the beginning, NGXP has been shaped by one ambition:</p>
              <p className="font-display text-xl font-semibold text-[#0A0B10] pl-5 border-l-4 border-[#7B2CBF]">
                to build technology that does not simply support business operations, but helps improve them.
              </p>
              <p>That belief continues to guide how we think, design, engineer, and deliver.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SECTION 4 & 5: VISION + MISSION ===== */}
      <section className="bg-[#F8F9FA] py-14 md:py-20">
        <div className="container-ngxp">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="ngxp-card reveal space-y-4">
              <div className="icon-badge"><Eye className="w-6 h-6" /></div>
              <h2 className="font-display text-3xl font-bold text-[#0A0B10]">Our <span className="gradient-text-glow">Vision</span></h2>
              <p className="text-gray-600 leading-relaxed">To become a globally respected technology company that engineers intelligent systems for the next generation of enterprise and industrial operations.</p>
              <p className="text-gray-600 leading-relaxed">We envision a future where organizations are not limited by delayed reports, disconnected tools, manual decisions, and static systems.</p>
              <p className="text-gray-600 leading-relaxed">Instead, they operate with clarity, intelligence, automation, simulation, and real-time decision support.</p>
              <p className="font-semibold text-[#0A0B10]">NGXP is working toward that future.</p>
            </div>
            <div className="ngxp-card reveal space-y-4">
              <div className="icon-badge"><Target className="w-6 h-6" /></div>
              <h2 className="font-display text-3xl font-bold text-[#0A0B10]">Our <span className="gradient-text-glow">Mission</span></h2>
              <p className="text-gray-600 leading-relaxed">Our mission is to help organizations solve complex operational challenges through intelligent, scalable, and practical technology systems.</p>
              <p className="text-sm font-semibold text-[#0A0B10] uppercase tracking-widest">We focus on building solutions that improve:</p>
              <div className="grid grid-cols-2 gap-2">
                {MISSION_ITEMS.map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-gray-600">
                    <CheckCircle2 className="w-4 h-4 text-[#7B2CBF] flex-shrink-0" />
                    {item}
                  </div>
                ))}
              </div>
              <p className="text-sm text-gray-500 italic">We do not approach technology as a checklist of features. We approach it as a business transformation tool.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SECTION 6: WHAT WE BELIEVE ===== */}
      <section className="bg-[#0A0B10] text-white py-14 md:py-20 relative overflow-hidden">
        <div className="absolute inset-0 dark-grid-pattern opacity-40" />
        <div className="glow-blob bg-[#7B2CBF]/30 w-[500px] h-[500px] top-0 right-0 pulse-glow" />
        <div className="glow-blob bg-[#00A8E8]/20 w-[400px] h-[400px] bottom-0 left-0 pulse-glow" />
        <div className="container-ngxp relative z-10">
          <div className="max-w-3xl reveal">
            <span className="overline">What We Believe</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight">
              Technology should be <span className="gradient-text-glow">clear, useful, intelligent,</span> and measurable.
            </h2>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6 reveal">
            {[
              { icon: Heart, text: "A system should not be built only to exist. It should help people work better, leaders decide faster, teams reduce errors, and organizations grow stronger." },
              { icon: Lightbulb, text: "The best enterprise systems are created when engineering, design, intelligence, business understanding, and operational reality come together." },
              { icon: Compass, text: "That is the mindset behind NGXP – where every decision is driven by what creates real, lasting value for the organizations we serve." },
            ].map((b, i) => {
              const Icon = b.icon;
              return (
                <div key={i} className="believe-card rounded-2xl border border-white/10 bg-white/5 p-6 space-y-3">
                  <div className="believe-icon w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <p className="text-white/80 text-sm leading-relaxed">{b.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== SECTION 7: HOW WE THINK ===== */}
      <section className="bg-white py-14 md:py-20">
        <div className="container-ngxp">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5 reveal">
              <span className="overline">Our Approach</span>
              <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[#0A0B10] leading-tight">
                Our Thinking <span className="gradient-text-glow">Approach</span>
              </h2>
              <p className="mt-5 text-gray-600 leading-relaxed">NGXP is not built around one narrow technology category. We think in terms of business outcomes, system architecture, operational flow, and long-term value.</p>
              <p className="mt-4 text-gray-600 leading-relaxed">This approach helps us build systems that are not only technically strong, but also meaningful to the people and businesses using them.</p>
            </div>
            <div className="lg:col-span-7 reveal">
              <p className="text-sm font-semibold text-[#0A0B10] uppercase tracking-widest mb-5">Before we build, we ask:</p>
              <div className="space-y-3">
                {THINKING_QUESTIONS.map((q, i) => (
                  <div
                    key={i}
                    className="thinking-card flex items-start gap-3 p-4 rounded-xl bg-[#F8F9FA] border border-gray-100"
                    style={{ animationDelay: `${i * 0.08}s` }}
                  >
                    <span className="text-xs font-mono font-bold text-[#7B2CBF] mt-0.5 flex-shrink-0">{String(i + 1).padStart(2, "0")}</span>
                    <p className="text-sm text-gray-700 leading-relaxed">{q}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SECTION 8: OUR TEAM ===== */}
      <section className="bg-[#F8F9FA] py-14 md:py-20">
        <div className="container-ngxp">
          <div className="max-w-3xl reveal">
            <span className="overline">Our Team</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[#0A0B10] leading-tight">
              A Multidisciplinary Team for <span className="gradient-text-glow">Complex Technology Challenges</span>
            </h2>
          </div>
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-6 reveal space-y-5 text-gray-600 leading-relaxed">
              <p>NGXP is powered by a growing team of engineers, designers, developers, architects, 3D specialists, AI practitioners, IoT and embedded system professionals, and enterprise solution experts.</p>
              <p>Our strength is not just in having different technical skills. Our strength is in combining those skills to solve problems that require more than one discipline.</p>
              <p>Complex enterprise challenges often need many capabilities working together – data, software, intelligence, visualization, field systems, user experience, and operational logic.</p>
              <p className="font-semibold text-[#0A0B10]">That is where NGXP is built to perform.</p>
            </div>
            <div className="lg:col-span-6 reveal">
              <div className="grid grid-cols-2 gap-4">
                {TEAM_SKILLS.map((skill, i) => (
                  <div
                    key={i}
                    className="skill-card flex items-center gap-2 p-3 rounded-xl bg-white border border-gray-100 shadow-sm"
                    style={{ animationDelay: `${i * 0.1}s` }}
                  >
                    <span className="skill-dot w-2 h-2 rounded-full bg-gradient-to-r from-[#7B2CBF] to-[#FF007F] flex-shrink-0" />
                    <span className="skill-label text-sm font-medium text-[#0A0B10]">{skill}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SECTION 9: HOW WE WORK ===== */}
      <section className="bg-white py-14 md:py-20">
        <div className="container-ngxp">
          <div className="max-w-3xl reveal">
            <span className="overline">How We Work</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[#0A0B10]">
              We work as a <span className="gradient-text-glow">strategic technology partner</span>
            </h2>
            <p className="mt-5 text-gray-600 leading-relaxed">Our process is built around clarity, alignment, execution, and continuous improvement – not only as a development vendor.</p>
          </div>
          <div className="mt-12 space-y-5 reveal">
            {HOW_WE_WORK_STEPS.map((s, i) => (
              <div key={i} className="work-step-card grid grid-cols-1 md:grid-cols-12 gap-4 items-start p-6 rounded-2xl bg-[#F8F9FA] border border-gray-100" style={{ animationDelay: `${i * 0.1}s` }}>
                <div className="md:col-span-1">
                  <span className="work-step-num font-mono text-2xl font-bold text-[#7B2CBF]">{s.num}</span>
                </div>
                <div className="md:col-span-11">
                  <h3 className="font-display text-lg font-semibold text-[#0A0B10]">{s.title}</h3>
                  <p className="mt-1 text-sm text-gray-600 leading-relaxed">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SECTION 10: WHAT MAKES US DIFFERENT ===== */}
      <section className="bg-[#F8F9FA] py-14 md:py-20">
        <div className="container-ngxp">
          <div className="max-w-3xl reveal">
            <span className="overline">Our Difference</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[#0A0B10]">
              What Makes <span className="gradient-text-glow">NGXP Different</span>
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 reveal">
            {DIFFERENTIATORS.map((d, i) => (
              <div key={i} className="ngxp-card space-y-3">
                <div className="flex items-center gap-3">
                  <span className="font-mono text-sm font-bold text-[#7B2CBF]">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="font-display text-base font-semibold text-[#0A0B10] leading-snug">{d.title}</h3>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">{d.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SECTION 11: OUR COMMITMENT ===== */}
      <section className="bg-white py-14 md:py-20">
        <div className="container-ngxp">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5 reveal">
              <span className="overline">Our Commitment</span>
              <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[#0A0B10] leading-tight">
                Our Commitment <span className="gradient-text-glow">to Clients</span>
              </h2>
              <p className="mt-5 text-gray-600 leading-relaxed">We are committed to building systems that are practical, reliable, intelligent, and aligned with real business outcomes.</p>
              <p className="mt-4 text-gray-600 leading-relaxed">We aim to become a trusted technology partner for organizations that want to move confidently into the next generation of intelligent operations.</p>
            </div>
            <div className="lg:col-span-7 reveal">
              <p className="text-sm font-semibold text-[#0A0B10] uppercase tracking-widest mb-5">When clients work with NGXP, they can expect:</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {COMMITMENT_ITEMS.map((item, i) => (
                  <div key={i} className="commitment-card flex items-center gap-3 p-4 rounded-xl bg-[#F8F9FA] border border-gray-100" style={{ animationDelay: `${i * 0.07}s` }}>
                    <CheckCircle2 className="commitment-icon w-5 h-5 text-[#7B2CBF] flex-shrink-0" />
                    <span className="text-sm text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SECTION 12: WHERE WE ARE GOING ===== */}
      <section className="bg-[#0A0B10] text-white py-14 md:py-20 relative overflow-hidden">
        <div className="absolute inset-0 dark-grid-pattern opacity-40" />
        <div className="glow-blob bg-[#FF007F]/20 w-[500px] h-[500px] -top-40 -right-32 pulse-glow" />
        <div className="glow-blob bg-[#00A8E8]/20 w-[400px] h-[400px] bottom-0 left-0 pulse-glow" />
        <div className="container-ngxp relative z-10">
          <div className="max-w-3xl reveal">
            <span className="overline">Our Future Direction</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight">
              Where We Are <span className="gradient-text-glow">Going</span>
            </h2>
          </div>
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-8 reveal">
            <div className="space-y-5 text-white/80 leading-relaxed">
              <p>NGXP is building toward a future where enterprises can operate with greater intelligence, clarity, and control.</p>
              <p>Our direction is focused on advanced enterprise intelligence, AI-native systems, connected operations, digital twins, immersive training, simulation-led decision-making, and intelligent command environments.</p>
            </div>
            <div className="space-y-5 text-white/80 leading-relaxed">
              <p>We are not only building for today's requirements.</p>
              <p className="font-display text-xl text-white font-semibold">We are building for the future of how organizations will operate, train, monitor, decide, and grow.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SECTION 13: FINAL CTA ===== */}
      <section className="bg-white py-14 md:py-18">
        <div className="container-ngxp">
          <div className="rounded-3xl bg-gradient-to-br from-[#00A8E8] via-[#7B2CBF] to-[#FF007F] p-10 md:p-16 text-white relative overflow-hidden">
            <div className="absolute inset-0 dark-grid-pattern opacity-30" />
            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 items-center gap-8">
              <div className="lg:col-span-8">
                <h3 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Build the Future with NGXP</h3>
                <p className="mt-3 text-white/85 max-w-2xl leading-relaxed">
                  If your organization is looking for a technology partner who can understand complex challenges, engineer intelligent systems, and support long-term business transformation, NGXP Technologies is ready to collaborate.
                </p>
              </div>
              <div className="lg:col-span-4 flex lg:justify-end flex-wrap gap-3">
                <Link to="/services" data-testid="about-capabilities-cta" className="inline-flex items-center gap-2 rounded-full bg-white/20 border border-white/30 text-white px-6 py-3 text-sm font-semibold hover:-translate-y-0.5 transition-transform">
                  Explore Our Capabilities <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/contact#contact-form" data-testid="about-final-cta" className="inline-flex items-center gap-2 rounded-full bg-white text-[#0A0B10] px-6 py-3 text-sm font-semibold hover:-translate-y-0.5 transition-transform">
                  Start a Strategic Discussion <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
            <p className="relative z-10 mt-8 text-white/50 text-xs text-center">
              NGXP Technologies - Built to engineer intelligent systems for the next generation of enterprise operations.
            </p>
          </div>
        </div>
      </section>

    </div>
  );
}
